import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, BookOpen, Calendar, Users, CreditCard, Link2, Shield, LayoutGrid } from 'lucide-react';

const SECTIONS = [
  {
    id: 'booking-actions',
    icon: LayoutGrid,
    title: 'Booking Actions menu (talent view)',
    faqs: [
      ['What is the Booking Actions menu?', 'When your talent open a booking, they tap the three-dot Actions sheet to upload photos, submit recaps, file reimbursements, view the dress code, grooming, instructions, payment details, add the shift to their calendar, or release the gig.'],
      ['Where does the Dress Code your talent sees come from?', 'It\'s the exact dress code you selected when posting the gig — image, title, description, and checklist all flow through to the Actions → Dress Code panel.'],
      ['Where do Grooming requirements come from?', 'The grooming choices you select at gig creation (tattoo, facial hair, hair, piercings) appear on the talent\'s Actions → Grooming and Booking Details → Grooming panels.'],
      ['Why does this matter?', 'Surfacing your job-brief content directly on each booking reduces dress-code violations and helps talent show up exactly the way you expect.'],
    ],
  },
  {
    icon: Calendar,
    title: 'Posting gigs',
    faqs: [
      ['How do I post a new gig?', 'Dashboard → Post a Gig. Fill venue, date/time, role, rate, and how many talent you need. Save → the gig appears in our matching queue immediately.'],
      ['What\'s the difference between Flex and Premium?', 'Flex = broadest pool, fastest to fill, standard rates. Premium = hand-vetted talent, higher bill rate, usually filled in under 4 hours.'],
      ['Can I request a specific talent?', 'Yes. On the shift detail → Staffing → Invite by name. If they accept, they\'re booked as Primary.'],
    ],
  },
  {
    icon: Users,
    title: 'Managing bookings',
    faqs: [
      ['When should I confirm my roster?', 'We recommend locking it 48 hours before the event. The Confirmation widget will nudge you.'],
      ['Someone dropped — what now?', 'Our system auto-promotes a Backup if any are assigned. Otherwise we re-broadcast to the invite radius. You\'ll see status change in the Staffing panel.'],
    ],
  },
  {
    icon: Shield,
    title: 'Attendance & compliance',
    faqs: [
      ['How do I verify talent showed up?', 'Shift detail → Attendance & Compliance. Each row shows clock-in time, selfie, GPS pin, and distance from the venue.'],
      ['Can I edit a clock-in time?', 'Yes — hover the time and click the pencil icon. Every edit is audit-logged with your email + reason.'],
    ],
  },
  {
    icon: CreditCard,
    title: 'Invoices & billing',
    faqs: [
      ['When do I get billed?', 'Weekly, based on completed shifts. Invoices land in your Billing tab with a PDF + line-item breakdown.'],
      ['Why is an amount different from what I quoted?', 'Overtime, manual clock adjustments, or added extras. Each invoice line links to the originating shift so you can audit the source.'],
    ],
  },
  {
    icon: Link2,
    title: 'Direct booking links',
    faqs: [
      ['A talent sent me a "booking link" — is it real?', 'Yes. Talent can share /book/<name> URLs. The request shows up here AND is logged to our Admin queue. We take 15% service fee on direct bookings.'],
    ],
  },
];

const ClientHelp = () => {
  const navigate = useNavigate();
  // Iter-234 — auto-scroll to the section matching the URL hash
  // (e.g. /client/help#booking-actions from the What's New modal).
  React.useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      requestAnimationFrame(() =>
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      );
    }
  }, []);
  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-help-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="client-help-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-cyan-600" />
            Docs & FAQs
          </h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.title} id={s.id || undefined} className={s.id ? 'scroll-mt-20' : undefined}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className="w-4 h-4 text-cyan-600" />
                  {s.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {s.faqs.map(([q, a], i) => (
                  <div key={i}>
                    <div className="font-medium text-sm text-gray-900">{q}</div>
                    <div className="text-sm text-gray-600 mt-0.5">{a}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}

        <Card>
          <CardContent className="py-4 text-center text-sm text-gray-600">
            Need more help? Email{' '}
            <a href="mailto:support@ievntur.com" className="text-cyan-700 hover:underline">
              support@ievntur.com
            </a>{' '}
            or call your account manager.
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientHelp;
